import {
  IonButton,
  IonIcon,
  IonInput,
  IonModal,
  IonRouterLink,
  IonText
} from '@ionic/react';
import React, { useMemo, useState } from 'react';

import ButtonContainer from '@/components/common/buttonContainer/ButtonContainer';
import Spacer from '@/components/common/spacer/Spacer';
import SelectModal from '@/components/modals/selectModal/SelectModal';
import DurationSummary from '@/components/summary/durationSummary/DurationSummary';
import { useScheduleFlow } from '@/contexts/ScheduleFlowContext';
import useToast from '@/hooks/useToast';
import { PURPOSE_JOIN_SEPARATOR, SELECT_MODAL_OTHERS } from '@/utils/constants';
import { formatDentistName } from '@/utils/helpers/formatDentistName';
import { formatMobileNumber } from '@/utils/helpers/formatMobileNumber';
import { formatName } from '@/utils/helpers/formatName';
import { isClinicName } from '@/utils/helpers/isClinicName';
import { ROUTES } from '@/utils/routes';
import { arrowForwardCircleOutline } from 'ionicons/icons';

import './SelectPurpose.css';

interface SelectPurposeProps {
  presentingElement?: HTMLElement | undefined;
}

const SelectPurpose: React.FC<SelectPurposeProps> = ({ presentingElement }) => {
  const {
    clinic,
    selectedDentist: dentist,
    purpose,
    services,
    setPurpose,
    othersPurpose,
    setOthersPurpose,
    fetchSelectedDentistSchedule: onSelectPurpose
  } = useScheduleFlow();
  const displayToast = useToast();

  const isUsingClinicName = useMemo(
    () => isClinicName(`${dentist?.first_name} ${dentist?.last_name}`),
    [dentist?.first_name, dentist?.last_name]
  );

  const [hasError, setHasError] = useState<boolean>(false);
  const [lastSavedPurpose, setLastSavedPurpose] = useState<string | null>(null);
  const [isSelectModalOpen, setIsSelectModalOpen] = useState<boolean>(false);

  const copy = {
    subHeader: 'TELL US WHAT YOU NEED',
    header: (
      <>
        Select the <IonText color="primary">purpose</IonText> of your
        appointment
      </>
    ),
    message: `Please indicate the reason for your visit ${isUsingClinicName ? 'at' : 'with'} ${formatDentistName(
      {
        dentist,
        returnDentistFullName: true
      }
    )}.`,
    buttonText: 'Next',
    inputLabel: 'Purpose of your Appointment',
    inputPlaceholder: 'Example: Braces Adjustment',
    modalTitle: (
      <>
        Select <IonText color="primary">purpose</IonText>
      </>
    ),
    othersLabel: 'Others, please specify...',
    othersPlaceholder: 'Enter purpose (optional)'
  };

  const options = services.map((option) => option.name);
  const isSelected = (value: string) =>
    purpose?.split(PURPOSE_JOIN_SEPARATOR).includes(value) || false;
  const isPreSelected = (value: string) =>
    lastSavedPurpose?.split(PURPOSE_JOIN_SEPARATOR).includes(value) || false;
  const onSelect = (value: string, isSelected: boolean) => {
    if (purpose) {
      if (isSelected) {
        setPurpose(
          purpose
            .split(',')
            .map((item) => item.trim())
            .filter((item) => item !== value)
            .join(PURPOSE_JOIN_SEPARATOR)
        );
      } else {
        setPurpose(purpose + PURPOSE_JOIN_SEPARATOR + value);
      }
    } else {
      setPurpose(value);
    }
  };
  const onDismiss = () => {
    setIsSelectModalOpen(false);
    setLastSavedPurpose(purpose);
  };
  const onNext = () => {
    if (purpose) {
      setHasError(false);
      onSelectPurpose();
    } else {
      setHasError(true);
      displayToast({
        message: 'Please select a valid purpose.',
        duration: 5000,
        position: 'bottom',
        positionAnchor: 'nextButton'
      });
    }
  };

  const onInput = (event: CustomEvent) => {
    const value = event.detail.value;
    setOthersPurpose(value);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      onNext();
    }
  };

  return (
    <div id="selectPurpose">
      <Spacer top={32} bottom={24}>
        <h4 id="subHeader">{copy.subHeader}</h4>
        <h1 id="header">{copy.header}</h1>
        <p id="message">{copy.message}</p>
      </Spacer>
      <div id="form">
        <div className={`input ${hasError ? 'error' : ''}`}>
          <IonInput
            id="purposeInput"
            label={copy.inputLabel}
            labelPlacement="stacked"
            value={purpose}
            placeholder={copy.inputPlaceholder}
            onClick={() => {
              setHasError(false);
              setIsSelectModalOpen(true);
            }}
            readonly
          />
        </div>
        {purpose === SELECT_MODAL_OTHERS && (
          <div className="input">
            <IonInput
              id="othersInput"
              label={copy.othersLabel}
              labelPlacement="stacked"
              value={othersPurpose}
              placeholder={copy.othersPlaceholder}
              onIonInput={(event: CustomEvent) => onInput(event)}
              onKeyDown={onKeyDown}
            />
          </div>
        )}
      </div>
      {purpose ? (
        <Spacer top={24}>
          <DurationSummary />
        </Spacer>
      ) : (
        <Spacer top={8}>
          <p id="helpPrompt">
            Need help? Call{' '}
            <IonText color="primary">
              {formatMobileNumber(clinic?.mobile_number)}
            </IonText>
          </p>
        </Spacer>
      )}
      <Spacer bottom={102} />
      <ButtonContainer>
        <IonButton
          id="nextButton"
          onClick={onNext}
          expand="block"
          fill="solid"
          size="default"
          color="primary"
          strong={true}
        >
          <IonIcon slot="start" icon={arrowForwardCircleOutline}></IonIcon>
          {copy.buttonText}
        </IonButton>
      </ButtonContainer>

      <SelectModal
        isOpen={isSelectModalOpen}
        presentingElement={presentingElement}
        title={copy.modalTitle}
        options={options}
        isSelected={isSelected}
        isPreSelected={isPreSelected}
        onSelect={onSelect}
        onDismiss={onDismiss}
        multiple={true}
      />
    </div>
  );
};

export default SelectPurpose;
